import React, { useEffect, useState, useMemo } from "react";
import { Box, keyframes } from "@chakra-ui/react";
import logo from "assets/img/logo.png";

// ============================================
// KEYFRAME ANIMATIONS (same style as SignIn)
// ============================================

const float = keyframes`
  0%, 100% { transform: translateY(0px) rotate(0deg); }
  50% { transform: translateY(-20px) rotate(3deg); }
`;

const pulse = keyframes`
  0%, 100% { opacity: 0.4; transform: scale(1); }
  50% { opacity: 0.8; transform: scale(1.05); }
`;

const shimmer = keyframes`
  0% { background-position: -200% 0; }
  100% { background-position: 200% 0; }
`;

const logoEntrance = keyframes`
  0% { 
    opacity: 0; 
    transform: scale(0.5) rotate(-10deg); 
  }
  60% { 
    opacity: 1;
    transform: scale(1.1) rotate(3deg); 
  }
  100% { 
    opacity: 1; 
    transform: scale(1) rotate(0deg); 
  }
`;

const logoFloat = keyframes`
  0%, 100% { 
    transform: translateY(0); 
  }
  50% { 
    transform: translateY(-8px); 
  }
`;

const glowPulse = keyframes`
  0%, 100% { 
    opacity: 0.4;
    transform: translate(-50%, -50%) scale(1);
  }
  50% { 
    opacity: 0.7;
    transform: translate(-50%, -50%) scale(1.1);
  }
`;

const ringExpand = keyframes`
  0% { 
    transform: scale(0.8); 
    opacity: 0.6;
  }
  100% { 
    transform: scale(2); 
    opacity: 0;
  }
`;

const orbitRing = keyframes`
  0% { 
    transform: rotate(0deg); 
  }
  100% { 
    transform: rotate(360deg); 
  }
`;

const starTwinkle = keyframes`
  0%, 100% { 
    opacity: 0.1;
    transform: scale(0.8);
  }
  50% { 
    opacity: 0.8;
    transform: scale(1.2);
  }
`;

const fadeOutZoom = keyframes`
  0% { 
    opacity: 1; 
    transform: scale(1); 
  }
  100% { 
    opacity: 0; 
    transform: scale(1.3); 
  }
`;



// ============================================
// COMPONENT
// ============================================

const Loading = ({ isExiting = false }) => {
  const [showContent, setShowContent] = useState(false);
  const [entranceComplete, setEntranceComplete] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setShowContent(true), 50);
    const entranceTimer = setTimeout(() => setEntranceComplete(true), 800);
    return () => {
      clearTimeout(timer);
      clearTimeout(entranceTimer);
    };
  }, []);

  // Generate random stars for background
  const stars = useMemo(() => {
    return Array.from({ length: 40 }, (_, i) => ({
      id: i,
      left: `${Math.random() * 100}%`,
      top: `${Math.random() * 100}%`,
      size: Math.random() * 2 + 1,
      delay: Math.random() * 3,
      duration: Math.random() * 2 + 2,
    }));
  }, []);

  return (
    <Box
      minH="100vh"
      w="100%"
      position="fixed"
      top="0"
      left="0"
      right="0"
      bottom="0"
      overflow="hidden"
      bg="#0a0d14"
      zIndex="9999"
      opacity={isExiting ? 0 : 1}
      transition="opacity 0.5s ease-in-out"
    >
      {/* Animated Background (same as SignIn) */}
      <Box position="absolute" top="0" left="0" right="0" bottom="0" zIndex="0">
        {/* Base gradient */}
        <Box
          position="absolute"
          top="0"
          left="0"
          right="0"
          bottom="0"
          bg="linear-gradient(to bottom right, #0a0d14, #0f1720, #0a0d14)"
        />
        <Box
          position="absolute"
          top="0"
          left="30%"
          w="40%"
          h="50%"
          bg="radial-gradient(ellipse at center, rgba(34, 197, 94, 0.15) 0%, transparent 70%)"
          filter="blur(40px)"
        />
        <Box
          position="absolute"
          bottom="0"
          right="20%"
          w="50%"
          h="50%"
          bg="radial-gradient(ellipse at center, rgba(16, 185, 129, 0.1) 0%, transparent 70%)"
          filter="blur(40px)"
        />
        
        {/* Floating orbs */}
        <Box
          position="absolute"
          top="10%"
          left="5%"
          w="300px"
          h="300px"
          borderRadius="full"
          bg="radial-gradient(circle, rgba(34, 197, 94, 0.2) 0%, transparent 70%)"
          filter="blur(40px)"
          animation={`${float} 8s ease-in-out infinite`}
        />
        <Box
          position="absolute"
          bottom="20%"
          right="10%"
          w="400px"
          h="400px"
          borderRadius="full"
          bg="radial-gradient(circle, rgba(74, 222, 128, 0.15) 0%, transparent 70%)"
          filter="blur(60px)"
          animation={`${float} 10s ease-in-out infinite reverse`}
        />
        <Box
          position="absolute"
          top="50%"
          left="50%"
          transform="translate(-50%, -50%)"
          w="600px"
          h="600px"
          borderRadius="full"
          bg="radial-gradient(circle, rgba(16, 185, 129, 0.08) 0%, transparent 60%)"
          filter="blur(80px)"
          animation={`${pulse} 6s ease-in-out infinite`}
        />
        
        {/* Grid pattern */}
        <Box
          position="absolute"
          top="0"
          left="0"
          right="0"
          bottom="0"
          opacity="0.03"
          backgroundImage={`
            linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px),
            linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)
          `}
          backgroundSize="80px 80px"
        />

        {/* Animated lines */}
        <Box
          position="absolute"
          top="30%"
          left="0"
          right="0"
          h="1px"
          background="linear-gradient(90deg, transparent, rgba(34, 197, 94, 0.3), transparent)"
          animation={`${shimmer} 3s linear infinite`}
          backgroundSize="200% 100%"
        />
        <Box
          position="absolute"
          top="70%"
          left="0"
          right="0"
          h="1px"
          background="linear-gradient(90deg, transparent, rgba(74, 222, 128, 0.2), transparent)"
          animation={`${shimmer} 4s linear infinite 1s`}
          backgroundSize="200% 100%"
        />

        {/* Twinkling stars */}
        {stars.map((star) => (
          <Box
            key={star.id}
            position="absolute"
            left={star.left}
            top={star.top}
            width={`${star.size}px`}
            height={`${star.size}px`}
            borderRadius="full"
            bg="white"
            animation={`${starTwinkle} ${star.duration}s ease-in-out infinite`}
            sx={{ animationDelay: `${star.delay}s` }}
          />
        ))}
      </Box>

      {/* Content wrapper with exit animation */}
      <Box
        position="absolute"
        inset="0"
        animation={isExiting ? `${fadeOutZoom} 0.5s ease-in-out forwards` : undefined}
        display="flex"
        alignItems="center"
        justifyContent="center"
        zIndex="1"
      >
        {/* Logo container */}
        <Box
          position="relative"
          opacity={showContent ? 1 : 0}
          animation={showContent 
            ? entranceComplete 
              ? `${logoFloat} 3s ease-in-out infinite`
              : `${logoEntrance} 0.8s cubic-bezier(0.34, 1.56, 0.64, 1) forwards`
            : undefined
          }
        >
          {/* Expanding pulse rings */}
          {[0, 1, 2].map((i) => (
            <Box
              key={i}
              position="absolute"
              top="50%"
              left="50%"
              transform="translate(-50%, -50%)"
            >
              <Box
                width="80px"
                height="80px"
                border="1px solid rgba(34, 197, 94, 0.4)"
                borderRadius="full"
                animation={`${ringExpand} 3s ease-out infinite`}
                sx={{ animationDelay: `${i * 1}s` }}
                pointerEvents="none"
              />
            </Box>
          ))}

          {/* Orbiting ring */}
          <Box
            position="absolute"
            top="50%"
            left="50%"
            transform="translate(-50%, -50%)"
          >
            <Box
              width="160px"
              height="160px"
              borderRadius="full"
              animation={`${orbitRing} 8s linear infinite`}
              sx={{
                border: "1px dashed rgba(34, 197, 94, 0.2)",
              }}
              _before={{
                content: '""',
                position: "absolute",
                top: "-4px",
                left: "50%",
                width: "8px",
                height: "8px",
                bg: "#4ade80",
                borderRadius: "full",
                boxShadow: "0 0 15px rgba(74, 222, 128, 0.8)",
                marginLeft: "-4px",
              }}
            />
          </Box>

          {/* Glow behind logo (same style as SignIn) */}
          <Box
            position="absolute"
            top="50%"
            left="50%"
            transform="translate(-50%, -50%)"
            w="150px"
            h="150px"
            borderRadius="full"
            bg="radial-gradient(circle, rgba(34, 197, 94, 0.4) 0%, rgba(34, 197, 94, 0.1) 50%, transparent 70%)"
            filter="blur(20px)"
            animation={`${glowPulse} 3s ease-in-out infinite`}
          />

          {/* Logo image - same size as SignIn (120px) */}
          <img
            src={logo}
            width="80px"
            alt="logo"
            style={{ 
              position: "relative", 
              zIndex: 1,
              filter: "drop-shadow(0 0 20px rgba(34, 197, 94, 0.4))"
            }}
          />
        </Box>
      </Box>
    </Box>
  );
};

export default Loading;
